// Validación de geometría y datos del cohete. Ver docs/theory/rocket-model.md.
import { attachedLength, layoutStack, type Layout } from './geometry';
import { getMaterial } from './materials';
import { MOTOR_FIT_TOLERANCE, type MotorResolver } from './motor';
import type { Attached, Component, MotorMountMark, Rocket, StackComponent } from './types';

export type Severity = 'error' | 'warning';

export interface Issue {
  readonly severity: Severity;
  /** Id del componente afectado, o `rocket` para el cohete completo. */
  readonly componentId: string;
  readonly field?: string;
  readonly message: string;
}

export interface ValidateOptions {
  /** Resolutor de motores del catálogo; si se omite, no se validan las dimensiones del motor. */
  readonly motors?: MotorResolver;
}

const positive = (v: number): boolean => Number.isFinite(v) && v > 0;
const nonNegative = (v: number): boolean => Number.isFinite(v) && v >= 0;

class Collector {
  readonly issues: Issue[] = [];
  error(componentId: string, message: string, field?: string): void {
    this.issues.push(field === undefined ? { severity: 'error', componentId, message } : { severity: 'error', componentId, field, message });
  }
  warn(componentId: string, message: string, field?: string): void {
    this.issues.push(field === undefined ? { severity: 'warning', componentId, message } : { severity: 'warning', componentId, field, message });
  }
  requirePositive(id: string, field: string, value: number, label: string): void {
    if (!positive(value)) this.error(id, `${label} debe ser un número mayor que cero`, field);
  }
}

function checkCommon(c: Collector, comp: Component, seen: Set<string>): void {
  if (comp.id === '') c.error(comp.id, 'El componente no tiene id', 'id');
  else if (seen.has(comp.id)) c.error(comp.id, `Id duplicado: ${comp.id}`, 'id');
  seen.add(comp.id);
  if (comp.massOverride !== undefined && !nonNegative(comp.massOverride)) {
    c.error(comp.id, 'La masa indicada debe ser un número mayor o igual que cero', 'massOverride');
  }
}

function checkMaterial(c: Collector, id: string, materialId: string, kind: 'bulk' | 'surface' | 'line', massOverride: number | undefined): void {
  if (massOverride !== undefined) return;
  const m = getMaterial(materialId);
  if (m === undefined) c.error(id, `Material desconocido: ${materialId}`, 'material');
  else if (m.kind !== kind) c.error(id, `El material ${materialId} no es del tipo ${kind === 'bulk' ? 'volumétrico' : kind === 'line' ? 'de cuerda' : 'de superficie'}`, 'material');
}

function checkWall(c: Collector, id: string, thickness: number, outerRadius: number, filled = false): void {
  c.requirePositive(id, 'wallThickness', thickness, 'El espesor de pared');
  if (positive(thickness) && positive(outerRadius) && !filled && thickness >= outerRadius) {
    c.error(id, 'El espesor de pared debe ser menor que el radio exterior', 'wallThickness');
  }
}

function innerDiameter(comp: StackComponent): number {
  if (comp.type === 'nose-cone') return 0;
  if (comp.type === 'body-tube') return comp.outerDiameter - 2 * comp.wallThickness;
  return Math.min(comp.foreDiameter, comp.aftDiameter) - 2 * comp.wallThickness;
}

/** Marca de portamotor de un tubo: voladizo, retardo y que el motor quepa en el tubo. */
function checkMotorMark(c: Collector, id: string, mark: MotorMountMark, length: number, innerDiameter: number, opts: ValidateOptions): void {
  if (mark.overhang !== undefined && !nonNegative(mark.overhang)) c.error(id, 'El voladizo del motor debe ser mayor o igual que cero', 'overhang');
  if (mark.motor === undefined) return;
  if (!nonNegative(mark.motor.ejectionDelay)) c.error(id, 'El retardo de eyección debe ser mayor o igual que cero', 'motor');
  const profile = opts.motors?.(mark.motor.motorId);
  if (opts.motors !== undefined && profile === undefined) c.error(id, `Motor no encontrado: ${mark.motor.motorId}`, 'motor');
  if (profile === undefined) return;
  if (profile.length > length + (mark.overhang ?? 0) + 1e-9) c.error(id, 'El motor es más largo que su soporte', 'motor');
  if (profile.diameter > innerDiameter + MOTOR_FIT_TOLERANCE) c.error(id, 'El motor es más ancho que su soporte', 'motor');
}

function checkAttached(c: Collector, a: Attached, parent: Layout, opts: ValidateOptions, seen: Set<string>): void {
  checkCommon(c, a, seen);
  const length = attachedLength(a);
  if (a.position.anchor !== 'front' && a.position.anchor !== 'aft') c.error(a.id, 'Anclaje inválido', 'position');
  if (!nonNegative(a.position.offset)) c.error(a.id, 'El desplazamiento debe ser mayor o igual que cero', 'position');
  if (positive(length) && length > parent.component.length) {
    c.error(a.id, 'El componente es más largo que el cuerpo que lo contiene', 'length');
  } else if (positive(length) && nonNegative(a.position.offset) && a.position.offset + length > parent.component.length) {
    c.error(a.id, 'El componente se sale del cuerpo que lo contiene', 'position');
  }

  switch (a.type) {
    case 'fin-set':
      if (!Number.isInteger(a.finCount) || a.finCount < 3 || a.finCount > 8) c.error(a.id, 'El número de aletas debe ser un entero entre 3 y 8', 'finCount');
      c.requirePositive(a.id, 'rootChord', a.rootChord, 'La cuerda de raíz');
      if (!nonNegative(a.tipChord)) c.error(a.id, 'La cuerda de punta debe ser mayor o igual que cero', 'tipChord');
      c.requirePositive(a.id, 'span', a.span, 'La envergadura');
      c.requirePositive(a.id, 'thickness', a.thickness, 'El espesor');
      if (!Number.isFinite(a.sweep)) c.error(a.id, 'La flecha debe ser un número finito', 'sweep');
      if (a.crossSection !== undefined && !['square', 'rounded', 'airfoil'].includes(a.crossSection)) c.error(a.id, 'Sección de aleta desconocida', 'crossSection');
      checkMaterial(c, a.id, a.material, 'bulk', a.massOverride);
      break;
    case 'inner-tube': {
      c.requirePositive(a.id, 'length', a.length, 'La longitud');
      c.requirePositive(a.id, 'innerDiameter', a.innerDiameter, 'El diámetro interior');
      c.requirePositive(a.id, 'wallThickness', a.wallThickness, 'El espesor de pared');
      checkMaterial(c, a.id, a.material, 'bulk', a.massOverride);
      const outer = a.innerDiameter + 2 * a.wallThickness;
      if (positive(outer) && outer > innerDiameter(parent.component)) c.error(a.id, 'El tubo interior no cabe en el cuerpo', 'innerDiameter');
      if (a.motorMount !== undefined) checkMotorMark(c, a.id, a.motorMount, a.length, a.innerDiameter, opts);
      break;
    }
    case 'parachute':
      c.requirePositive(a.id, 'diameter', a.diameter, 'El diámetro');
      c.requirePositive(a.id, 'packedLength', a.packedLength, 'La longitud empacada');
      if (!(Number.isFinite(a.dragCoefficient) && a.dragCoefficient > 0 && a.dragCoefficient <= 3)) c.error(a.id, 'El coeficiente de arrastre debe estar entre 0 (excluido) y 3', 'dragCoefficient');
      if (a.deployment.event === 'altitude' && !positive(a.deployment.altitude)) c.error(a.id, 'La altitud de despliegue debe ser mayor que cero', 'deployment');
      checkMaterial(c, a.id, a.material, 'surface', a.massOverride);
      if (a.lines !== undefined) {
        if (!Number.isInteger(a.lines.count) || a.lines.count < 0) c.error(a.id, 'El número de cuerdas debe ser un entero mayor o igual que cero', 'lines');
        if (!nonNegative(a.lines.length)) c.error(a.id, 'La longitud de las cuerdas debe ser mayor o igual que cero', 'lines');
        checkMaterial(c, a.id, a.lines.material, 'line', a.massOverride);
      }
      break;
    case 'shock-cord':
      c.requirePositive(a.id, 'length', a.length, 'La longitud');
      if (!nonNegative(a.cordLength)) c.error(a.id, 'La longitud del cordón debe ser mayor o igual que cero', 'cordLength');
      checkMaterial(c, a.id, a.material, 'line', a.massOverride);
      break;
    case 'mass-component':
      c.requirePositive(a.id, 'length', a.length, 'La longitud');
      if (!nonNegative(a.mass)) c.error(a.id, 'La masa debe ser mayor o igual que cero', 'mass');
      break;
    case 'centering-ring':
    case 'bulkhead':
    case 'engine-block':
      c.requirePositive(a.id, 'length', a.length, 'La longitud');
      c.requirePositive(a.id, 'outerDiameter', a.outerDiameter, 'El diámetro exterior');
      if (!nonNegative(a.innerDiameter)) c.error(a.id, 'El diámetro interior debe ser mayor o igual que cero', 'innerDiameter');
      else if (positive(a.outerDiameter) && a.innerDiameter >= a.outerDiameter) c.error(a.id, 'El diámetro interior debe ser menor que el exterior', 'innerDiameter');
      if (positive(a.outerDiameter) && a.outerDiameter > innerDiameter(parent.component) + 1e-9) c.warn(a.id, 'El diámetro exterior supera el interior del tubo', 'outerDiameter');
      checkMaterial(c, a.id, a.material, 'bulk', a.massOverride);
      break;
    case 'launch-lug':
      c.requirePositive(a.id, 'length', a.length, 'La longitud');
      c.requirePositive(a.id, 'outerDiameter', a.outerDiameter, 'El diámetro exterior');
      checkWall(c, a.id, a.wallThickness, a.outerDiameter / 2);
      checkMaterial(c, a.id, a.material, 'bulk', a.massOverride);
      break;
  }
}

export function validateRocket(rocket: Rocket, opts: ValidateOptions = {}): Issue[] {
  const c = new Collector();
  const seen = new Set<string>();

  if (rocket.stack.length === 0) {
    c.error('rocket', 'El cohete no tiene componentes de cuerpo: no hay donde acoplar aletas ni motor', 'stack');
    return c.issues;
  }
  if (rocket.stack[0]!.type !== 'nose-cone') c.warn('rocket', 'El primer componente no es un cono de ojiva', 'stack');

  const layout = layoutStack(rocket);
  layout.forEach((entry, i) => {
    const comp = entry.component;
    checkCommon(c, comp, seen);
    c.requirePositive(comp.id, 'length', comp.length, 'La longitud');
    switch (comp.type) {
      case 'nose-cone':
        c.requirePositive(comp.id, 'baseDiameter', comp.baseDiameter, 'El diámetro de la base');
        checkWall(c, comp.id, comp.wallThickness, comp.baseDiameter / 2, comp.filled);
        if (comp.shape === 'power' && !(comp.powerExponent !== undefined && comp.powerExponent > 0 && comp.powerExponent <= 1)) {
          c.error(comp.id, 'El exponente de la serie de potencia debe estar entre 0 (excluido) y 1', 'powerExponent');
        }
        checkMaterial(c, comp.id, comp.material, 'bulk', comp.massOverride);
        if (comp.shoulder !== undefined) {
          const sh = comp.shoulder;
          if (!nonNegative(sh.length)) c.error(comp.id, 'La longitud del hombro debe ser mayor o igual que cero', 'shoulder');
          c.requirePositive(comp.id, 'shoulder', sh.diameter, 'El diámetro del hombro');
          if (positive(sh.diameter) && sh.diameter > comp.baseDiameter + 1e-9) c.error(comp.id, 'El hombro es más ancho que la base de la ojiva', 'shoulder');
          c.requirePositive(comp.id, 'shoulder', sh.thickness, 'El espesor del hombro');
          if (positive(sh.thickness) && positive(sh.diameter) && sh.thickness > sh.diameter / 2 + 1e-9) c.error(comp.id, 'El espesor del hombro no puede superar su radio', 'shoulder');
        }
        break;
      case 'body-tube':
        c.requirePositive(comp.id, 'outerDiameter', comp.outerDiameter, 'El diámetro exterior');
        checkWall(c, comp.id, comp.wallThickness, comp.outerDiameter / 2);
        checkMaterial(c, comp.id, comp.material, 'bulk', comp.massOverride);
        if (comp.motorMount !== undefined) checkMotorMark(c, comp.id, comp.motorMount, comp.length, comp.outerDiameter - 2 * comp.wallThickness, opts);
        break;
      case 'transition':
        c.requirePositive(comp.id, 'foreDiameter', comp.foreDiameter, 'El diámetro delantero');
        c.requirePositive(comp.id, 'aftDiameter', comp.aftDiameter, 'El diámetro trasero');
        checkWall(c, comp.id, comp.wallThickness, Math.min(comp.foreDiameter, comp.aftDiameter) / 2);
        checkMaterial(c, comp.id, comp.material, 'bulk', comp.massOverride);
        break;
    }
    if (i > 0) {
      const prev = layout[i - 1]!.component;
      const aftPrev = prev.type === 'nose-cone' ? prev.baseDiameter : prev.type === 'body-tube' ? prev.outerDiameter : prev.aftDiameter;
      const foreThis = comp.type === 'nose-cone' ? 0 : comp.type === 'body-tube' ? comp.outerDiameter : comp.foreDiameter;
      if (foreThis > 0 && positive(aftPrev) && Math.abs(aftPrev - foreThis) > 1e-9) {
        c.warn(comp.id, `El diámetro no coincide con el del componente anterior (${aftPrev} m frente a ${foreThis} m)`, 'diameter');
      }
    }
    if (comp.type !== 'nose-cone') comp.children.forEach((child) => checkAttached(c, child, entry, opts, seen));
  });

  return c.issues;
}

export const hasErrors = (issues: readonly Issue[]): boolean => issues.some((i) => i.severity === 'error');
